import { Modal, Upload, message } from 'antd';
import './ProjectForm.css';
import { useForm } from 'react-hook-form';
import { PUBLIC_URL } from '../../__common/env';
import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useMainContext } from '../../hooks/useMainContext';
import {
    ProjectScope,
    ClosetColor,
    HandleStyle,
    HandleColor,
    RodColor,
    DoorColor,
    DrawerColor,
    DoorStyle,
} from '../../config';
import { Controller } from 'react-hook-form';
import { extractName } from '../../utils/uiUtils';

const { Dragger } = Upload;

export const ProjectForm = observer(() => {
    const { closetManager, viewManager, matrixManager } = useMainContext();
    const {
        register,
        control,
        handleSubmit,
        formState: { errors, setError },
        clearErrors,
        watch,
        setValue,
        reset,
        getValues,
    } = useForm({
        shouldFocusError: false,
        defaultValues: {
            projectName: '',
            projectDescription: '',
            projectCreator: viewManager.userData.name
                ? viewManager.userData.name
                : extractName(viewManager.userData.email),
            projectScope: ProjectScope.ALL_CLOSETS,
            customScope: '',
            color: ClosetColor.WHITE,
            handleStyle: HandleStyle.T_BAR,
            handleColor: HandleColor.CHROME,
            rodColor: RodColor.CHROME,
            doorColor: DoorColor.WHITE,
            drawerColor: DrawerColor.WHITE,
            drawerStyle: DoorStyle.SLAB,
            doorStyle: DoorStyle.SLAB,
            legs: false,
            shelf: false,
            topDoors: false,
            bottomDoors: false,
            pdfFile: null,
        },
    });

    const [isCustomScope, setIsCustomScope] = useState(false);
    const selectedScope = watch('projectScope');

    useEffect(() => {
        setIsCustomScope(selectedScope === ProjectScope.CUSTOM);
    }, [selectedScope]);

    useEffect(() => {
        if (viewManager.openForm && viewManager.formData) {
            Object.keys(viewManager.formData).forEach((key) =>
                setValue(key, viewManager.formData[key] || ''),
            );
        }
    }, [viewManager.openForm, setValue, viewManager.formData]);

    const onSubmit = (data) => {
        let hasError = false;

        // Check for required fields
        if (!data.projectName) {
            setError('projectName', {
                type: 'required',
                message: 'Project Name is required',
            });
            message.error('Please enter a Project Name');
            hasError = true;
        } else {
            clearErrors('projectName');
        }

        if (!data.projectCreator) {
            setError('projectCreator', {
                type: 'required',
                message: 'Developer Name is required',
            });
            message.error('Please enter a Developer Name');
            hasError = true;
        } else {
            clearErrors('projectCreator');
        }

        if (!data.projectScope) {
            setError('projectScope', {
                type: 'required',
                message: 'Scope is required',
            });
            message.error('Please select a Scope');
            hasError = true;
        } else {
            clearErrors('projectScope');
        }

        if (data.projectScope === ProjectScope.CUSTOM && !data.customScope) {
            setError('customScope', {
                type: 'required',
                message: 'Custom Scope is required',
            });
            message.error('Please enter a Custom Scope');
            hasError = true;
        } else if (data.projectScope === ProjectScope.CUSTOM) {
            clearErrors('customScope');
        }

        if (!data.pdfFile?.file) {
            setError('pdfFile', {
                type: 'required',
                message: 'PDF file is required',
            });
            message.error('Please upload a PDF file');
            hasError = true;
        } else {
            clearErrors('pdfFile');
        }

        if (closetManager.pdfBuffer) {
            message.error('A PDF file is already being processed');
            hasError = true;
        }

        if (hasError) {
            return;
        }

        try {
            viewManager.setPdfFile(data.pdfFile.file);
            closetManager.setPdfBuffer(data.pdfFile.file);
            matrixManager.setPdfBuffer(data.pdfFile.file);
            viewManager.setFormData(data);
            viewManager.setCurrentPage('workSpace');
            message.success('Project created successfully');
        } catch (error) {
            message.error('Failed to create project. Please try again.');
            console.error('Error creating project:', error);
        }
    };

    const handleClose = () => {
        // Clean up any object URLs before closing
        const pdfFile = getValues('pdfFile');
        if (pdfFile?.preview) {
            URL.revokeObjectURL(pdfFile.preview);
        }
        viewManager.setOpenForm(false);
        reset();
    };

    const validatePdfFile = (file) => {
        if (!file) return 'Please upload a file.';
        if (file.type !== 'application/pdf') {
            message.error('Only PDF files are allowed.');
            return 'Only PDF files are allowed.';
        }
        return true;
    };

    return (
        <Modal
            className="select-popup-modal"
            centered
            width={'800px'}
            height={'auto'}
            onCancel={handleClose}
            footer={null}
            open={viewManager.openForm}
            closable={false}>
            <div
                onClick={handleClose}
                className="absolute bg-theme-white cursor-pointer z-10 top-[-34px] right-[-12px] h-[28px] w-[28px] flex justify-center items-center rounded-full border-[1.5px] border-theme-button-bg">
                <img src={`${PUBLIC_URL}/assets/icons/close_icon.svg`} alt="" />
            </div>
            <div className="flex flex-col gap-[24px] relative max-h-[90vh] overflow-y-auto px-[24px] ">
                <div className="pb-[8px] border-b-[1px] border-theme-button-bg">
                    <p className="font-favorit text-[14px] leading-[16px] text-theme-icon-color font-normal">
                        Create Project
                    </p>
                </div>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="flex flex-col gap-[16px]">
                        {/* Project Name */}
                        <InputField
                            id="project-name"
                            label="Project Name*"
                            placeholder="Enter Project Name"
                            register={register}
                            name="projectName"
                            error={errors.projectName}
                            required="Project Name is required"
                        />

                        {/* Project Description */}
                        <InputField
                            id="project-description"
                            label="Project Description"
                            placeholder="Enter Project Description"
                            register={register}
                            name="projectDescription"
                            error={errors.projectDescription}
                        />

                        {/* Developer */}
                        <InputField
                            id="project-creator"
                            label="Developer*"
                            placeholder="Enter Developer Name"
                            register={register}
                            name="projectCreator"
                            error={errors.projectCreator}
                            required="Developer Name is required"
                        />

                        {/* Project Scope */}
                        <DropdownField
                            id="project-scope"
                            label="Scope*"
                            options={ProjectScope}
                            register={register}
                            name="projectScope"
                            error={errors.projectScope}
                            required="Scope is required"
                        />

                        {/* Custom Scope */}
                        {isCustomScope && (
                            <InputField
                                id="custom-scope"
                                label="Custom Scope*"
                                placeholder="Enter Custom Scope"
                                register={register}
                                name="customScope"
                                error={errors.customScope}
                                required="Custom Scope is required"
                            />
                        )}

                        {/* Closet Color */}
                        <DropdownField
                            id="closet-color"
                            label="Closet Color"
                            options={ClosetColor}
                            register={register}
                            name="color"
                        />

                        {/* Handle Color, Handle Style, Rod Color */}
                        <div className="grid grid-cols-3 gap-4">
                            <DropdownField
                                id="handle-color"
                                label="Handle Color"
                                options={HandleColor}
                                register={register}
                                name="handleColor"
                            />
                            <DropdownField
                                id="handle-style"
                                label="Handle Style"
                                options={HandleStyle}
                                register={register}
                                name="handleStyle"
                            />
                            <DropdownField
                                id="rod-color"
                                label="Rod Color"
                                options={RodColor}
                                register={register}
                                name="rodColor"
                            />
                        </div>

                        {/* Drawer and Door Fields */}
                        <div className="grid grid-cols-4 gap-2">
                            <DropdownField
                                id="drawer-color"
                                label="Drawer Color"
                                options={DrawerColor}
                                register={register}
                                name="drawerColor"
                            />
                            <DropdownField
                                id="drawer-style"
                                label="Drawer Style"
                                options={DoorStyle}
                                register={register}
                                name="drawerStyle"
                            />
                            <DropdownField
                                id="door-color"
                                label="Door Color"
                                options={DoorColor}
                                register={register}
                                name="doorColor"
                            />
                            <DropdownField
                                id="door-style"
                                label="Door Style"
                                options={DoorStyle}
                                register={register}
                                name="doorStyle"
                            />
                        </div>

                        {/* Additional Options */}
                        <div>
                            <label className="block text-[14px] font-favorit text-theme-black font-normal mb-2">
                                Additional Options:
                            </label>
                            <div className="grid grid-cols-4 gap-4">
                                <CheckboxField
                                    register={register}
                                    name="legs"
                                    label="Legs"
                                />
                                <CheckboxField
                                    register={register}
                                    name="shelf"
                                    label="Shelf"
                                />
                                <CheckboxField
                                    register={register}
                                    name="topDoors"
                                    label="Top Doors"
                                />
                                <CheckboxField
                                    register={register}
                                    name="bottomDoors"
                                    label="Bottom Doors"
                                />
                            </div>
                        </div>

                        {/* Upload PDF */}
                        <div className="flex flex-col">
                            <div className="part-file">
                                <div className="form-field form-group flex gap-[16px] items-center h-[56px]">
                                    <label
                                        htmlFor="pdfFile"
                                        className="font-favorit w-[110px] text-[14px] leading-[16px] text-theme-black font-normal">
                                        Upload PDF*
                                    </label>

                                    <Controller
                                        name="pdfFile"
                                        control={control}
                                        rules={{
                                            required: 'Please upload a file.',
                                            validate: (value) => {
                                                if (!value?.file) {
                                                    return 'Please upload a file.';
                                                }
                                                return validatePdfFile(
                                                    value.file,
                                                );
                                            },
                                        }}
                                        render={({
                                            field: { value, onChange },
                                        }) => (
                                            <div
                                                style={{
                                                    height: '56px',
                                                    width: '255px',
                                                }}>
                                                <Dragger
                                                    name="pdfFile"
                                                    multiple={false}
                                                    accept=".pdf"
                                                    beforeUpload={(file) => {
                                                        if (
                                                            file.type !==
                                                            'application/pdf'
                                                        ) {
                                                            message.error(
                                                                'Only PDF files are allowed.',
                                                            );
                                                            return Upload.LIST_IGNORE;
                                                        }
                                                        return false;
                                                    }}
                                                    onDrop={(e) => {
                                                        const files =
                                                            e.dataTransfer
                                                                .files;
                                                        if (files.length > 0) {
                                                            const file =
                                                                files[0];
                                                            if (
                                                                file.type !==
                                                                'application/pdf'
                                                            ) {
                                                                message.error(
                                                                    'Only PDF files are allowed.',
                                                                );
                                                                e.preventDefault();
                                                            }
                                                        }
                                                    }}
                                                    onChange={({ file }) => {
                                                        if (file) {
                                                            const newValue = {
                                                                file,
                                                                preview:
                                                                    URL.createObjectURL(
                                                                        file,
                                                                    ),
                                                            };
                                                            onChange(newValue);
                                                            setValue(
                                                                'pdfFile',
                                                                newValue,
                                                            );
                                                            clearErrors(
                                                                'pdfFile',
                                                            );
                                                        }
                                                    }}
                                                    onRemove={() => {
                                                        if (value?.preview) {
                                                            URL.revokeObjectURL(
                                                                value.preview,
                                                            );
                                                        }
                                                        onChange(null);
                                                        setValue(
                                                            'pdfFile',
                                                            null,
                                                        );
                                                    }}
                                                    showUploadList={false}
                                                    fileList={
                                                        value?.file
                                                            ? [value.file]
                                                            : []
                                                    }>
                                                    {!value?.preview ? (
                                                        <div className="flex gap-[16px]">
                                                            <div className="flex justify-center items-center h-[32px] w-[32px]">
                                                                <img
                                                                    src={`${PUBLIC_URL}/assets/icons/file_icon.png`}
                                                                    alt=""
                                                                    className="w-[24px] h-[24px] object-contain"
                                                                />
                                                            </div>
                                                            <div className="flex flex-col gap-[4px]">
                                                                <h1 className="font-favorit text-[14px] leading-[16px] text-theme-black font-normal">
                                                                    Drag or drop
                                                                    your PDF
                                                                    File
                                                                </h1>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div
                                                            className="file-preview-actions"
                                                            style={
                                                                actionsStyle
                                                            }>
                                                            <button className="bg-theme-button-bg font-favorit font-normal w-fit text-[14px] leading-[16px] text-theme-black flex items-center h-[32px] py-[4px] px-[24px] w-auto rounded-[8px] transition-all duration-300 ease-in-out group">
                                                                {value?.file
                                                                    ?.name ||
                                                                    'Change File'}
                                                            </button>
                                                            <div
                                                                onClick={(
                                                                    e,
                                                                ) => {
                                                                    e.stopPropagation();
                                                                    if (
                                                                        value?.preview
                                                                    ) {
                                                                        URL.revokeObjectURL(
                                                                            value.preview,
                                                                        );
                                                                    }
                                                                    onChange(
                                                                        null,
                                                                    );
                                                                    setValue(
                                                                        'pdfFile',
                                                                        null,
                                                                    );
                                                                }}
                                                                className="absolute bg-theme-white cursor-pointer z-10 top-[-20px] right-[-10px] h-[18px] w-[18px] flex justify-center items-center rounded-full border-[1.5px] border-theme-button-bg">
                                                                <img
                                                                    src={`${PUBLIC_URL}/assets/icons/close_icon.svg`}
                                                                    alt=""
                                                                    className="w-[12px] h-[12px] object-contain"
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </Dragger>
                                            </div>
                                        )}
                                    />
                                </div>
                                {errors.pdfFile && (
                                    <p className="text-red-500 text-sm mt-1 ml-[126px]">
                                        {errors.pdfFile.message}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="mt-[24px]">
                        <button
                            type="submit"
                            className="w-full bg-theme-orange font-favorit text-[14px] text-white flex items-center justify-center h-[48px] px-[24px] rounded-[2.25rem] hover:bg-theme-orange-hover transition-all">
                            Create
                        </button>
                    </div>
                </form>
            </div>
        </Modal>
    );
});

/* Reusable Components */
const InputField = ({
    id,
    label,
    register,
    name,
    placeholder,
    error,
    required,
}) => (
    <div className="flex flex-col">
        <label
            htmlFor={id}
            className="font-favorit text-[14px] text-theme-black font-normal">
            {label}
        </label>
        <input
            id={id}
            placeholder={placeholder}
            className="input-class"
            {...register(name, { required })}
        />
        {error && <p className="text-red-500 text-sm">{error.message}</p>}
    </div>
);

const DropdownField = ({ id, label, options, register, name }) => (
    <div className="flex flex-col">
        <label
            htmlFor={id}
            className="font-favorit text-[14px] text-theme-black font-normal">
            {label}
        </label>
        <select id={id} className="input-class" {...register(name)}>
            {Object.values(options).map((opt) => (
                <option key={opt} value={opt}>
                    {opt}
                </option>
            ))}
        </select>
    </div>
);

const CheckboxField = ({ register, name, label }) => (
    <label className="flex items-center gap-2">
        <input type="checkbox" {...register(name)} /> {label}
    </label>
);

const actionsStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    gap: '10px',
    width: '255px',
    height: '32px',
};
